import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import React, { useRef, useState, useEffect, useId } from 'react';
import { useRouter } from 'next/router';
import styles from './SearchBar.module.scss';
import useDictionary from 'lib/hooks/useDictionary';
import { SvgIcon } from 'components/helpers/SvgIcon';
import clsx from 'clsx';
import { SearchDropDownBase } from '../SearchDropDown/SearchDropDown';
import {
  pushSearchActionEvent,
  pushSearchIntentEvent,
} from 'components/helpers/Analytics/SearchAndFilters/SearchAndFiltersAnalytics';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { SiteSettings } from 'lib/page-props-factory/plugins/site-settings';
import {
  clearPendingSearchRouteAnnouncement,
  setPendingSearchRouteAnnouncement,
} from 'lib/a11y/searchRouteAnnouncement';

const parseMultilineValue = (value?: string) => {
  if (typeof value !== 'string') return [];

  return value
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const parsePresearchItems = (siteSettings: SiteSettings | null) => {
  const fieldValue = siteSettings?.PresearchItems?.value;

  const directStringLines = parseMultilineValue(
    typeof fieldValue === 'string' ? fieldValue : undefined
  );
  if (directStringLines.length) return directStringLines;

  if (!fieldValue || typeof fieldValue !== 'object') return [];

  const fieldRecord = fieldValue as Record<string, unknown>;
  const directValueLines = parseMultilineValue(
    typeof fieldRecord.value === 'string' ? fieldRecord.value : undefined
  );
  if (directValueLines.length) return directValueLines;

  const nestedSearchText = (fieldRecord.fields as Record<string, unknown> | undefined)
    ?.searchText as Record<string, unknown> | undefined;

  return parseMultilineValue(
    typeof nestedSearchText?.value === 'string' ? nestedSearchText.value : undefined
  );
};

const TYPEAHEAD_MIN_CHARS = 3;
const TYPEAHEAD_DEBOUNCE_MS = 200;
const TYPEAHEAD_CACHE_TTL_MS = 5 * 60 * 1000;

type CachedTypeaheadResult = {
  items: string[];
  expiresAt: number;
};

export function SearchBar() {
  const router = useRouter();
  const [searchValue, setSearchValue] = useState('');
  const [showFade, setShowFade] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [typeaheadItems, setTypeaheadItems] = useState<string[]>([]);
  const listboxId = useId();
  const lastInputWasKeyboard = useRef(false);
  const suppressNextFetch = useRef(false);
  const suppressNextFocusOpen = useRef(false);
  const typeaheadCacheRef = useRef(new Map<string, CachedTypeaheadResult>());
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const dropdownListRef = useRef<HTMLUListElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const clearButtonRef = useRef<HTMLButtonElement>(null);

  const { getDictionaryValue } = useDictionary();
  const isEditing = useIsEditing();
  const siteSettings = useSiteSettings();
  const { sitecoreContext } = useSitecoreContext();
  const isGMFS = sitecoreContext?.site?.name === 'GMFS';
  const presearchItems = parsePresearchItems(siteSettings);
  const showingResultText =
    getDictionaryValue('Components.Search.ShowingResult') || 'Showing results for';

  // Keep the search input in sync with the URL ?term= param
  useEffect(() => {
    if (router.isReady && typeof router.query.term === 'string') {
      setSearchValue(router.query.term);
    }
  }, [router.isReady, router.query.term]);

  // Debounced typeahead fetch — fires when searchValue reaches TYPEAHEAD_MIN_CHARS+
  useEffect(() => {
    if (!isFocused) {
      return;
    }

    if (suppressNextFetch.current) {
      suppressNextFetch.current = false;
      return;
    }
    if (searchValue.length < TYPEAHEAD_MIN_CHARS) {
      setTypeaheadItems([]);
      return;
    }

    const cacheKey = searchValue.trim().toLowerCase();
    const cachedResult = typeaheadCacheRef.current.get(cacheKey);
    if (cachedResult && cachedResult.expiresAt > Date.now()) {
      setTypeaheadItems(cachedResult.items);
      return;
    }

    if (cachedResult) {
      typeaheadCacheRef.current.delete(cacheKey);
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search/hints?searchTerm=${encodeURIComponent(searchValue)}`, {
          signal: controller.signal,
        });
        if (!res.ok) {
          return;
        }
        const data = await res.json();
        const allResults: { ResultType: string; DisplayText: string }[] = data?.Results ?? [];
        // GMFS accepts both Content and Search types; all other sites use Search type only
        const keywords = allResults
          .filter((r) =>
            isGMFS
              ? r.ResultType === 'Content' || r.ResultType === 'Search'
              : r.ResultType === 'Search'
          )
          .map((r) => r.DisplayText);
        typeaheadCacheRef.current.set(cacheKey, {
          items: keywords,
          expiresAt: Date.now() + TYPEAHEAD_CACHE_TTL_MS,
        });
        setTypeaheadItems(keywords);
      } catch {
        // fetch was aborted or failed — ignore silently
      }
    }, TYPEAHEAD_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [isFocused, isGMFS, searchValue]);

  const isPresearch = isFocused && searchValue.length === 0 && presearchItems.length > 0;
  const isTypeahead =
    isFocused && searchValue.length >= TYPEAHEAD_MIN_CHARS && typeaheadItems.length > 0;
  const showDropDown = isPresearch || isTypeahead;
  const activeItems = isTypeahead ? typeaheadItems : presearchItems;

  const closeSuggestions = React.useCallback(() => {
    setIsFocused(false);
    setTypeaheadItems([]);
  }, []);

  const placeHolderText = getDictionaryValue('Navigation.Search.SearchPlaceHolderText');
  const inputLabelText = getDictionaryValue('Navigation.Search.InputLabelText') || 'Search';
  const searchText = getDictionaryValue('Navigation.Search.SearchButton') || 'Search';
  const clearSearchText = getDictionaryValue('Navigation.Search.ClearButtonText') || 'Clear search';

  // Close dropdown when focus leaves both the input and the dropdown
  useEffect(() => {
    const list = dropdownListRef.current;
    if (!list) return;
    const handleFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (!next || (!list.contains(next) && next !== inputRef.current)) {
        setIsFocused(false);
      }
    };
    list.addEventListener('focusout', handleFocusOut);
    return () => list.removeEventListener('focusout', handleFocusOut);
  }, [showDropDown, closeSuggestions]);

  const checkOverflow = () => {
    const el = inputRef.current;
    if (!el) return;

    const TOLERANCE = 1;
    setShowFade(el.scrollWidth > el.clientWidth + TOLERANCE);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/^\s+/, '');
    setSearchValue(value);
    // Re-open dropdown when user types (e.g., after closing with Escape)
    setIsFocused(true);

    requestAnimationFrame(checkOverflow);
  };

  useEffect(() => {
    checkOverflow();
  }, [searchValue]);

  useEffect(() => {
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, []);

  const navigateToSearch = (term: string, searchType: 'Standard' | 'TypeAhead' | 'PreSearch') => {
    const normalizedTerm = term.trim();
    if (!normalizedTerm) return;

    closeSuggestions();

    pushSearchActionEvent({
      searchType,
      siteSearchPhrase: normalizedTerm,
    });

    // In Page Builder, use shallow replace to update query params without
    // navigating the editing iframe away from the Sitecore editing URL.
    if (isEditing) {
      router.replace(
        { pathname: router.pathname, query: { ...router.query, term: normalizedTerm, searchType } },
        undefined,
        { shallow: true }
      );
      return;
    }

    const searchUrl = `/search?term=${encodeURIComponent(normalizedTerm)}&searchType=${searchType}`;
    const isAlreadyOnSearchPage =
      router.pathname === '/search' || router.asPath.startsWith('/search');

    if (isAlreadyOnSearchPage) {
      clearPendingSearchRouteAnnouncement();
    } else {
      setPendingSearchRouteAnnouncement(`${showingResultText} "${normalizedTerm}"`);
    }

    // Shallow routing when already on /search; full navigation otherwise.
    if (isAlreadyOnSearchPage) {
      router.push(searchUrl, undefined, { shallow: true });
    } else {
      router.push(searchUrl);
    }
  };

  const handleSearch = () => navigateToSearch(searchValue, 'Standard');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSearch();
  };

  const clearInput = () => {
    setSearchValue('');
    inputRef.current?.focus();
  };
  useEffect(() => {
    const handleKeyDetect = (e: KeyboardEvent) => {
      if (e.key === 'Tab') lastInputWasKeyboard.current = true;
      if (e.key !== 'Tab') lastInputWasKeyboard.current = false;
    };

    const handleMouseDown = () => {
      lastInputWasKeyboard.current = false;
    };

    window.addEventListener('keydown', handleKeyDetect);
    window.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDetect);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className={clsx(styles.searchBox, showDropDown && styles.dropdownOpen)}
    >
      <div
        className={clsx(
          styles.searchField,
          showFade && searchValue && styles.showFade,
          lastInputWasKeyboard.current && styles.keyboardFocus
        )}
      >
        <span className={styles.searchIcon} aria-hidden="true">
          <SvgIcon className={styles.searchIconSvg} icon="search" size="sm" aria-hidden="true" />
        </span>
        <label htmlFor="searchInput" className="screenReaderText">
          {inputLabelText}
        </label>
        <input
          id="searchInput"
          ref={inputRef}
          role="combobox"
          aria-expanded={showDropDown}
          aria-haspopup="true"
          aria-controls={showDropDown ? listboxId : undefined}
          className={clsx(
            styles.searchInput,
            isFocused && lastInputWasKeyboard.current && styles.keyboardFocus
          )}
          type="text"
          autoComplete="off"
          placeholder={placeHolderText}
          value={searchValue}
          maxLength={250}
          onChange={handleInputChange}
          onClick={() => {
            pushSearchIntentEvent();
          }}
          onFocus={() => {
            if (suppressNextFocusOpen.current) {
              suppressNextFocusOpen.current = false;
              checkOverflow();
              return;
            }

            setIsFocused(true);
            checkOverflow();
          }}
          onBlur={() => {
            // Delay so focus can settle — keep dropdown open if focus moved into it
            setTimeout(() => {
              const active = document.activeElement;
              if (!dropdownListRef.current?.contains(active) && active !== inputRef.current) {
                closeSuggestions();
              }
            }, 0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && showDropDown && !e.shiftKey) {
              e.preventDefault();
              const firstOption =
                dropdownListRef.current?.querySelector<HTMLLIElement>('[role="option"]');
              firstOption?.focus();
            }
            if (e.key === 'Escape' && showDropDown) {
              e.preventDefault();
              closeSuggestions();
            }
          }}
          tabIndex={0}
        />

        {searchValue && (
          <button
            ref={clearButtonRef}
            type="button"
            className={styles.clearIcon}
            onClick={clearInput}
            aria-label={clearSearchText}
          >
            <SvgIcon className={styles.clearIconSvg} icon="clear" size="sm" />
          </button>
        )}
      </div>
      {showDropDown && (
        <SearchDropDownBase
          listRef={dropdownListRef}
          inputRef={inputRef}
          listboxId={listboxId}
          items={activeItems}
          clearButtonRef={clearButtonRef}
          nextFocusableRef={searchButtonRef}
          wrapperRef={formRef}
          // Typeahead items are pre-filtered by the API — don't re-filter in the dropdown.
          // Presearch items are static so query filtering/highlighting still applies.
          query={isPresearch ? searchValue : undefined}
          onDismiss={() => {
            suppressNextFocusOpen.current = true;
            closeSuggestions();
          }}
          onSelect={(value) => {
            suppressNextFetch.current = true;
            setSearchValue(value);
            navigateToSearch(value, isPresearch ? 'PreSearch' : 'TypeAhead');
            requestAnimationFrame(checkOverflow);
            inputRef.current?.focus();
          }}
        />
      )}
      <CtaButton
        ref={searchButtonRef}
        ctaStyle="none"
        ctaVariant="brand"
        type="submit"
        aria-label={searchText}
        className={clsx(styles.searchBtn, lastInputWasKeyboard.current && styles.keyboardFocusCta)}
      >
        {searchText}
      </CtaButton>
      <div aria-live="polite" className="screenReaderText">
        {showDropDown
          ? `${activeItems.length} suggestion${activeItems.length !== 1 ? 's' : ''} available`
          : ''}
      </div>
    </form>
  );
}
